"use client"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  WorkQueueRowActionsCell,
  WorkQueueStateCell,
} from "@/components/evaluation-dashboard/evaluation-term-table-cells"
import type { EvaluationWorkQueueRow } from "@/lib/mock/evaluation-work-queue"

interface OperationsTermWorkQueueCompactProps {
  rows: EvaluationWorkQueueRow[]
  className?: string
  onOpenWorkQueue: (row: EvaluationWorkQueueRow) => void
}

const RESPONSE_RATE_TARGET = 70

const ROW_DIVIDER = "border-b border-border/60 last:border-b-0"

function needsLowResponseBadge(row: EvaluationWorkQueueRow): boolean {
  return row.responseRate != null && row.responseRate < RESPONSE_RATE_TARGET
}

/** Condensed work queue for paired term columns — opens sheet on action. */
export function OperationsTermWorkQueueCompact({
  rows,
  className,
  onOpenWorkQueue,
}: OperationsTermWorkQueueCompactProps) {
  if (rows.length === 0) {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>
        No items in the work queue.
      </p>
    )
  }

  return (
    <div className={cn(className)}>
      <div className="overflow-x-auto rounded-lg border border-border/60">
        <table className="w-full min-w-[20rem] border-collapse text-sm">
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className={ROW_DIVIDER}>
                <td className="max-w-0 py-2 pl-3 pr-2 font-medium text-foreground">
                  <span className="block truncate">{row.subject}</span>
                </td>
                <td className="w-px whitespace-nowrap py-2 pl-2 pr-1 text-right align-middle">
                  {needsLowResponseBadge(row) ? (
                    <StatusBadge
                      label="Low response"
                      tone="warning"
                      size="sm"
                      icon="fa-arrow-trend-down"
                    />
                  ) : null}
                </td>
                <td className="w-px whitespace-nowrap py-2 px-1 text-right align-middle">
                  <WorkQueueStateCell row={row} size="sm" />
                </td>
                <td className="w-px whitespace-nowrap py-2 pr-3 pl-1 text-right align-middle">
                  <WorkQueueRowActionsCell
                    row={row}
                    compact
                    onAction={() => onOpenWorkQueue(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
